<template>
  <div class="account-listings">
    <b-row class="mb-3">
      <b-col
        :cols="12"
        :md="3"
        :lg="2">
        <account-listings-navigation/>
      </b-col>
      <b-col
        :cols="12"
        :md="9"
        :lg="10">
        <b-card
          v-if="!this.status"
          class="mb-3"
          no-body>
          <b-card-body>
            <h2 class="h5 mb-2">
              Viewing all Listings
            </h2>
            <p class="mb-2">
              You are viewing all Listings that you've
              created. Use the menu to filter by Listing
              status.
            </p>
            <p class="mb-0">
              Need help?
              <router-link
                to="/faq"
                target="_blank">
                <strong>
                  View our FAQ
                </strong>
              </router-link>
              to learn more about managing your Listings.
            </p>
          </b-card-body>
          <b-card-footer class="text-right">
            <b-button
              variant="outline-dark"
              class="bg-white"
              size="sm"
              to="/account/listings/new"
              squared>
              <font-awesome-icon
                class="mr-2"
                :icon="['fas', 'plus']"/>
              Create a new listing
            </b-button>
          </b-card-footer>
        </b-card>
        <b-card
          v-for="listing in listings"
          :key="`${status || 'all'}-${listing.id}`"
          class="mb-4 p-0"
          footer-tag="footer"
          no-body>
          <b-card-body>
            <b-row
              class="mt-2 mb-2 p-2">
              <b-col
                :cols="12"
                :lg="3"
                :md="2">
                <listing-image
                  class="mb-3"
                  :src="listing.imageUrl"/>
              </b-col>
              <b-col
                :cols="12"
                :lg="9"
                :md="10">
                <router-link
                  class="text-dark"
                  :to="`/listing/${listing.id}`">
                  <h2 class="h4 mb-3">
                    {{ listing.title }}
                  </h2>
                </router-link>
                <!-- Listing Status -->
                <div>
                  <!-- Draft -->
                  <p v-if="listing.status === 'Draft'">
                    This listing is a draft. When you're ready,
                    you can submit it to be posted on ZoopLocal.
                  </p>
                  <!-- Pending -->
                  <div v-else-if="listing.status === 'Pending'">
                    <p>
                      <b-badge variant="info">Pending Approval</b-badge>
                    </p>
                    <p>
                      You have submitted this listing for approval.
                      Once we finish reviewing your listing, it will
                      become public to ZoopLocal buyers.
                    </p>
                  </div>
                  <!-- Approved (live) -->
                  <div
                    v-else-if="listing.status === 'Approved'">
                    <p>
                      <b-badge variant="success">Active Listing</b-badge>
                    </p>
                    <p>
                      This listing is live on ZoopLocal until {{ listing.expiresAt | date }}.
                    </p>
                  </div>
                  <!-- Requested (has buyer) -->
                  <div
                    v-else-if="listing.status === 'Requested'">
                    <p>
                      <b-badge variant="success">Active Listing</b-badge>
                      <b-badge class="ml-2" variant="warning">Action Required</b-badge>
                    </p>
                    <strong>You have a buyer!</strong>
                    Go to
                    <router-link
                      class="font-weight-bold"
                      to="/account/purchases/selling/pending">
                      Buying and Selling
                    </router-link>
                    to see your Purchase Request.
                  </div>
                  <div
                    v-else-if="listing.status === 'In Progress'">
                    <p>
                      <b-badge variant="info">Sale Confirmed</b-badge>
                      <b-badge class="ml-2" variant="warning">Action Required</b-badge>
                    </p>
                    <strong>This listing has a buyer.</strong>
                    Go to
                    <router-link
                      class="font-weight-bold"
                      to="/account/purchases/selling/pending">
                      Buying and Selling
                    </router-link>
                    to message with the Buyer and complete delivery.
                  </div>
                  <div
                    v-else-if="listing.status === 'Completed'">
                    <p>
                      <b-badge variant="info">Sale Completed</b-badge>
                    </p>
                    <strong>This sale was completed {{ listing.updatedAt | relativeDate }}.</strong>
                    Go to
                    <router-link
                      class="font-weight-bold"
                      to="/account/purchases/selling/completed">
                      Buying and Selling
                    </router-link>
                    for the complete history of this sale.
                  </div>
                </div>
                <!-- Additional Info (views, expiration, etc.) -->
                <div>
                  <div
                    v-if="listing.status === 'Approved' || listing.status === 'Draft'">
                    <font-awesome-icon
                      size="xs"
                      :icon="['far', 'clock']"/>
                    Last updated on {{ listing.updatedAt | date }}
                  </div>
                  <div
                    v-if="listing.status === 'Pending'">
                    <font-awesome-icon
                      size="xs"
                      :icon="['far', 'clock']"/>
                    Submitted on {{ listing.updatedAt | date }}
                  </div>
                </div>

                <!-- Views on active listings -->
                <div
                  v-if="listing.status === 'Approved'">
                  <font-awesome-icon
                    size="xs"
                    :icon="['far', 'eye']"/>
                  {{ listing.views }} Views
                </div>

                <!-- Expiration Date -->
                <div
                  v-if="listing.status === 'Expired'">
                  <p>
                    <b-badge variant="danger">Expired</b-badge>
                  </p>
                  <p>
                    This listing expired on {{ listing.updatedAt | date }}. Select
                    <strong>Relist</strong> below to extend the duration of the
                    listing.
                  </p>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
          <b-card-footer class="text-right">
            <div>
              <b-button
                class="bg-white mr-2"
                variant="outline-primary"
                size="sm"
                :to="`/listing/${listing.id}`"
                target="_blank"
                squared>
                Preview
              </b-button>
              <b-button
                v-if="listing.status === 'Draft'"
                class="bg-white"
                variant="outline-dark"
                size="sm"
                :to="`/account/listing/${listing.id}`"
                squared>
                Edit
              </b-button>
              <!-- Requested (has buyer) -->
              <b-button
                v-if="listing.status === 'Requested'"
                class="bg-white"
                variant="outline-dark"
                size="sm"
                to="/account/purchases/selling/pending"
                squared>
                View Purchase Requests
              </b-button>
              <!-- Expired -->
              <b-button
                v-if="listing.status === 'Expired'"
                class="bg-white"
                variant="outline-dark"
                size="sm"
                squared
                @click="openRelistModal(listing.id)">
                Relist...
              </b-button>
              <a
                v-if="false"
                class="text-danger font-weight-bold mr-3"
                href="#">
                Delete...
              </a>
            </div>
          </b-card-footer>
        </b-card>
        <div
          v-if="listings.length === 0"
          class="account-listings__pagination text-center p-3">
          <h3 class="h5">
            No listings were found in this category.
          </h3>
          <p>
            Haven't created any listings yet?
            <router-link
              class="font-weight-bold"
              to="/faq">
              View our FAQ
            </router-link>
            or click below to get started.
          </p>
          <b-button
            variant="outline-dark"
            size="sm"
            to="/account/listings/new"
            squared>
            Create Listing
          </b-button>
        </div>
      </b-col>
    </b-row>


    <b-modal
      id="delete-modal"
      title="Are you sure?"
      body-class="text-center"
      footer-class="justify-content-center"
      size="sm"
      hide-header
      centered
      @ok="handleDelete">
      <div
        class="d-block text-center my-3">
        <img
          width="60px"
          src="/images/icons/icon-exclaim.png"
          draggable="false">
      </div>
      <h2 class="h3">
        Are you sure?
      </h2>
      <p class="text-muted">
      You are about to delete <strong>Pottery Barn Stools.</strong> This action cannot be undone.
      </p>
      <template
        v-slot:modal-footer="{ ok, cancel, hide }">
        <b-button
          size="sm"
          variant="danger"
          @click="ok()"
          squared>
          Yes, delete this listing
        </b-button>
        <b-button
          size="sm"
          variant="outline-dark"
          @click="cancel()"
          squared>
          Cancel
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'

import ListingImage from '@/components/images/ListingImage.vue'
import date from '@/filters/date.filter'
import relativeDate from '@/filters/relativeDate.filter'
import AccountListingsNavigation from './AccountListingsNavigation.vue'

export default {
  name: 'AccountListings',
  components: {
    AccountListingsNavigation,
    ListingImage,
  },
  filters: {
    date,
    relativeDate,
  },
  data() {
    return {
      loading: false,
      listings: [],
      tableProperties: {
        fields: ['id', 'title', 'status'],
      },
    }
  },
  methods: {
    handleDelete() {
      this.allListings = []
    },
    async openRelistModal(id) {
      const answer = await this.$bvModal.msgBoxConfirm('You will be taken to the checkout page to complete the relisting.', {
        title: 'Ready to relist?',
        okVariant: 'success',
      })

      if (answer === true) {
        this.$router.push({
          path: '/account/listings/checkout',
          query: { id },
        })
      }
    },
    async fetchListings() {
      this.loading = true
      this.listings = []

      try {
        const { data: { listings } } = await this.$http.get('/account/listings', {
          params: {
            status: this.status || undefined,
          },
        })
        this.listings = listings
      } catch (error) {
        console.error(error)
        Sentry.captureException(error)
      }
      this.loading = false
    },
  },
  watch: {
    '$route.params.status': function updateOnStatus(newValue, oldValue) {
      console.log(`Status changed from ${oldValue || 'all'} to ${newValue}`)
      this.fetchListings()
    },
  },
  computed: {
    status() {
      return this.$route.params.status
    },
  },
  mounted() {
    this.fetchListings()
  },
}
</script>

<style lang="scss">
.account-listings__listing {
  align-content: center;
  display: flex;
}

.account-listings__listing-footer {
  align-items: center;
  display: flex;
  justify-content: space-between;
}
</style>
